import React, { useState, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { useQueryClient } from '@tanstack/react-query';

import LandingPage from './pages/LandingPage';
import DemoPage from './pages/DemoPage';
import DemoRequestPage from './pages/DemoRequestPage';
import LegalPage from './pages/LegalPage';
import SupportPage from './pages/SupportPage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import PublicNotFound from './pages/PublicNotFound';
import MainLayout from './components/Layout/MainLayout';
import Dashboard from './pages/Dashboard';
import Owners from './pages/Owners';
import Pets from './pages/Pets';
import Appointments from './pages/Appointments';
import Examinations from './pages/Examinations';
import Vaccinations from './pages/Vaccinations';
import VaccineCard from './pages/VaccineCard';
import PatientReport from './pages/PatientReport';
import ClinicSettings from './pages/ClinicSettings';
import Statistics from './pages/Statistics';
import AccountSettings from './pages/AccountSettings';
import NotFound from './pages/NotFound';
import PageTransition from './components/Transitions/PageTransition';
import ErrorBoundary from './components/ErrorBoundary';
import CommandPalette from './components/CommandPalette/CommandPalette';
import Login from './pages/Login';
import { Pet, Owner, SessionUser } from './types';
import { supabase } from './services/mockData';
import { loadUserProfile } from './services/authProfile';

const SESSION_USER_KEY = 'vetklinik_user';

function parseStoredLocalUser(kind: 'env' | 'guest'): SessionUser | null {
  try {
    const raw = localStorage.getItem(SESSION_USER_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw) as SessionUser;
    if (u?.authProvider === kind && u.email) return u;
    return null;
  } catch {
    return null;
  }
}

function App() {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  // Cmd+K / Ctrl+K global kısayolu
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (user) setShowCommandPalette(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const applySession = async (session: Session | null) => {
      if (cancelled) return;

      if (session?.user) {
        const profile = await loadUserProfile(session.user.id);
        if (cancelled) return;
        if (!profile) {
          await supabase.auth.signOut();
          localStorage.removeItem('authToken');
          localStorage.removeItem(SESSION_USER_KEY);
          localStorage.removeItem('vetklinik_appts_last_viewed');
          queryClient.clear();
          setUser(null);
          return;
        }
        const next: SessionUser = {
          name: profile.displayName || session.user.email || '',
          email: session.user.email || '',
          role: 'Veteriner Hekim',
          clinicId: profile.clinicId,
          clinicName: profile.clinicName || null,
          authProvider: 'supabase',
        };
        setUser(next);
        localStorage.setItem(SESSION_USER_KEY, JSON.stringify(next));
        if (session.access_token) {
          localStorage.setItem('authToken', session.access_token);
        }
        return;
      }

      const token = localStorage.getItem('authToken');
      if (token?.startsWith('clinic-token')) {
        const envUser = parseStoredLocalUser('env');
        if (envUser) {
          setUser(envUser);
          return;
        }
      }
      if (token?.startsWith('guest-token')) {
        const guestUser = parseStoredLocalUser('guest');
        if (guestUser) {
          setUser(guestUser);
          return;
        }
      }

      localStorage.removeItem('authToken');
      localStorage.removeItem(SESSION_USER_KEY);
      localStorage.removeItem('vetklinik_appts_last_viewed');
      queryClient.clear();
      setUser(null);
    };

    void supabase.auth.getSession().then(({ data: { session } }) => applySession(session));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void applySession(session);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const handleLogin = (userData: SessionUser) => {
    // Önceki oturumdan kalan önbellek (başka klinik) yeni kullanıcıya görünmesin
    queryClient.clear();
    setUser(userData);
    localStorage.setItem(SESSION_USER_KEY, JSON.stringify(userData));
  };

  const handleLogout = async () => {
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem(SESSION_USER_KEY);
    localStorage.removeItem('vetklinik_appts_last_viewed');
    queryClient.clear();
    await supabase.auth.signOut();
    toast.success('Başarıyla çıkış yapıldı!');
  };

  const handlePetSelect = (pet: Pet & { owner?: Owner }) => {
    // Pets sayfasına yönlendir ve pet detayını göster
    toast.success(`${pet.name} seçildi! Pets sayfasına gidip detayları görebilirsin.`);
  };

  return (
    <ErrorBoundary>
    <ThemeProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Landing Page - Ana Sayfa */}
            <Route path="/" element={<LandingPage />} />
            {/* Herkese açık: etkileşimli arayüz (sahte veri), giriş yok */}
            <Route path="/demo" element={<DemoPage />} />
            {/* Herkese açık: iletişim / özel demo talep formu */}
            <Route path="/demo-request" element={<DemoRequestPage />} />
            {/* Herkese açık: yasal metinler (App Store gizlilik URL'i için) */}
            <Route path="/gizlilik" element={<LegalPage doc="privacy" />} />
            <Route path="/kvkk" element={<LegalPage doc="kvkk" />} />
            <Route path="/destek" element={<SupportPage />} />
            <Route path="/hesap-silme" element={<AccountDeletionPage />} />
            {/* Talepler localStorage + e-posta; URL’yi paylaşmayın — yalnızca siz bilin */}
            
            {/* Login Sayfası */}
            <Route path="/login" element={
              !user ? (
                <Login onLogin={handleLogin} />
              ) : (
                <Navigate to="/app" replace />
              )
            } />
            
            {/* Uygulama Sayfaları - /app altında */}
            <Route path="/app/*" element={
              user ? (
                <MainLayout user={user} onLogout={handleLogout} onPetSelect={handlePetSelect}>
                  <PageTransition>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/owners" element={<Owners />} />
                      <Route path="/pets" element={<Pets />} />
                      <Route path="/appointments" element={<Appointments />} />
                      <Route path="/examinations" element={<Examinations />} />
                      <Route path="/vaccinations" element={<Vaccinations />} />
                      <Route path="/vaccine-card" element={<VaccineCard user={user} />} />
                      <Route path="/patient-report" element={<PatientReport />} />
                      <Route path="/statistics" element={<Statistics />} />
                      <Route path="/clinic-settings" element={<ClinicSettings user={user} />} />
                      <Route path="/account" element={<AccountSettings user={user} />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </PageTransition>
                </MainLayout>
              ) : (
                <LandingPage />
              )
            } />
            
            {/* Eski route'ları yönlendir */}
            <Route path="/dashboard" element={<LandingPage />} />
            <Route path="/owners" element={<LandingPage />} />
            <Route path="/pets" element={<LandingPage />} />
            <Route path="/appointments" element={<LandingPage />} />
            <Route path="/examinations" element={<LandingPage />} />
            <Route path="/vaccinations" element={<LandingPage />} />
            <Route path="/vaccine-card" element={<LandingPage />} />
            <Route path="/settings" element={<LandingPage />} />
            {/* Tanımsız herkese açık adresler boş sayfa yerine 404 gösterir */}
            <Route path="*" element={<PublicNotFound />} />
          </Routes>

          {/* Global Cmd+K Command Palette */}
          {user && (
            <CommandPalette
              isOpen={showCommandPalette}
              onClose={() => setShowCommandPalette(false)}
            />
          )}

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#ffffff',
                color: '#111827',
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
